import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar as CalendarIcon } from "lucide-react";

/**
 * PeriodFilter — pill row with Week / Month / Quarter / Year / All / Custom.
 * Emits { key, start, end, label } via onChange.
 */
const preset = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
};

export const PRESETS = {
  week:    { label: "Week",    days: 7 },
  month:   { label: "Month",   days: 30 },
  quarter: { label: "Quarter", days: 90 },
  year:    { label: "Year",    days: 365 },
  all:     { label: "All time", days: null },
};

export function computeRange(key, custom) {
  if (key === "custom") {
    return { start: custom?.start || "", end: custom?.end || "", label: `${custom?.start || "?"} → ${custom?.end || "?"}` };
  }
  if (key === "all" || !PRESETS[key]) return { start: "", end: "", label: "All time" };
  const { start, end } = preset(PRESETS[key].days);
  return { start, end, label: PRESETS[key].label };
}

export default function PeriodFilter({ value, onChange, testid }) {
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [popOpen, setPopOpen] = useState(false);

  const pick = (k) => {
    const r = computeRange(k);
    onChange({ key: k, ...r });
  };

  const applyCustom = () => {
    if (!customStart || !customEnd) return;
    const r = computeRange("custom", { start: customStart, end: customEnd });
    onChange({ key: "custom", ...r });
    setPopOpen(false);
  };

  const btn = (k) => (
    <button
      key={k}
      onClick={() => pick(k)}
      data-testid={`${testid || "period"}-${k}`}
      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${value?.key === k ? "bg-[#1D63ED] text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}
    >
      {PRESETS[k].label}
    </button>
  );

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid || "period-filter"}>
      {["week", "month", "quarter", "year", "all"].map(btn)}
      <Popover open={popOpen} onOpenChange={setPopOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid={`${testid || "period"}-custom`}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors inline-flex items-center gap-1.5 ${value?.key === "custom" ? "bg-[#1D63ED] text-white" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}
          >
            <CalendarIcon className="w-3 h-3" />
            {value?.key === "custom" ? value.label : "Custom"}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 bg-white border-slate-200">
          <p className="text-xs font-medium text-slate-700 mb-2">Custom range</p>
          <div className="space-y-2">
            <div>
              <label className="text-[11px] text-slate-500">Start</label>
              <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="h-8" data-testid="custom-start" />
            </div>
            <div>
              <label className="text-[11px] text-slate-500">End</label>
              <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="h-8" data-testid="custom-end" />
            </div>
            <Button onClick={applyCustom} disabled={!customStart || !customEnd} className="w-full h-8 bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="apply-custom">
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
